import { User } from '../services/endpoints/people/user';
import { canSeeFinancials, isGlobal, isSuper } from './consts';

// =============================================================================
// PLACES DOMAIN
// =============================================================================

// -- Locations ----------------------------------------------------------------

/**
 * Check if user can create locations
 * Per RBAC Matrix: Super Admin, Supreme Admin
 */
export const canCreateLocation = (user: User | null): boolean => {
  if (!user) return false;
  return isSuper(user.user_type_id);
};

/**
 * Check if user can edit locations
 * Per RBAC Matrix: Super Admin, Supreme Admin
 */
export const canEditLocation = (user: User | null): boolean => {
  if (!user) return false;
  return isSuper(user.user_type_id);
};

/**
 * Check if user can view locations list
 * Per RBAC Matrix: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
 */
export const canViewLocations = (user: User | null): boolean => {
  if (!user) return false;
  return isGlobal(user.user_type_id) || canSeeFinancials(user.user_type_id);
};

/**
 * Check if user can access location financials (ledger)
 * Per RBAC Matrix: Admin (own location), Super Admin, Supreme Admin
 */
export const canAccessFinancialReports = (user: User | null, locationId: number): boolean => {
  if (!user) return false;
  return user.isSuper || (user.user_type_id === 2 && user.location_id === locationId);
};

// -- Organizations ------------------------------------------------------------

/**
 * Check if user can view/edit/create organizations
 * Per RBAC Matrix: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
 */
export const canViewOrganizations = (user: User | null): boolean => {
  if (!user) return false;
  // Instructors may VIEW orgs; API location-scopes them (see note in changeset).
  return isGlobal(user.user_type_id) || canSeeFinancials(user.user_type_id) || user.isInstructor;
};

// -- Territories --------------------------------------------------------------

/**
 * Check if user can view territories list
 * Per RBAC Matrix: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
 */
export const canViewTerritories = (user: User | null): boolean => {
  if (!user) return false;
  return isGlobal(user.user_type_id) || canSeeFinancials(user.user_type_id);
};

/**
 * Check if user can create/edit territories
 * Per RBAC Matrix: Super Admin, Supreme Admin
 */
export const canEditTerritory = (user: User | null): boolean => {
  if (!user) return false;
  return isSuper(user.user_type_id);
};

/**
 * Check if user can view territory financial reports
 * Per RBAC Matrix: Super Admin, Supreme Admin, Admin/Location Admin (own location only)
 */
export const canViewTerritoryReports = (
  user: User | null,
  territoryLocationId: number | null
): boolean => {
  if (!user || !territoryLocationId) return false;
  if (isSuper(user.user_type_id)) return true;
  if (canSeeFinancials(user.user_type_id)) return user.location_id === territoryLocationId;
  return false;
};

// -- Themes ------------------------------------------------------------------

/**
 * Check if user can view themes
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor, Global Admin (SF-280)
 */
export const canViewThemes = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor || user.isGlobal || user.isInstructor;
};

/**
 * Check if user can edit/create/delete themes
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor, Global Admin (SF-280)
 */
export const canEditThemes = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

// -- Venues -------------------------------------------------------------------

/**
 * Check if user can view/edit/create venues
 * Per RBAC Matrix: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
 */
export const canViewVenues = (user: User | null): boolean => {
  if (!user) return false;
  return isGlobal(user.user_type_id) || canSeeFinancials(user.user_type_id) || user.isInstructor;
};

/**
 * Check if user can view venue financial reports
 * Per RBAC Matrix: Super Admin, Supreme Admin, Admin (own location only)
 */
export const canViewVenueReports = (user: User | null, venueLocationId: number | null): boolean => {
  if (!user || !venueLocationId) return false;
  if (isSuper(user.user_type_id)) return true;
  if (user.user_type_id === 2) return user.location_id === venueLocationId;
  return false;
};

// =============================================================================
// CONTENT DOMAIN
// =============================================================================

// -- Kitchen Skills -----------------------------------------------------------

/**
 * Check if user can view kitchen skills
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor, Global Admin
 */
export const canViewKitchenSkills = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor || user.isGlobal;
};

// =============================================================================
// WEBSITE DOMAIN
// =============================================================================

// -- Pages --------------------------------------------------------------------

/**
 * Check if user can edit/create/delete pages
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditPages = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

/**
 * Check if user can view pages
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor, Global Admin
 */
export const canViewPages = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor || user.isGlobal;
};

// -- News Items ---------------------------------------------------------------

/**
 * Check if user can edit/create/delete news items
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditNewsItems = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

/**
 * Check if user can view news items
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor, Global Admin
 */
export const canViewNewsItems = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor || user.isGlobal;
};

// -- Galleries ----------------------------------------------------------------

/**
 * Check if user can edit/create/delete galleries
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditGalleries = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

// -- Snippets -----------------------------------------------------------------

/**
 * Check if user can edit/create/delete snippets
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditSnippets = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

// -- Team Profiles ------------------------------------------------------------

/**
 * Check if user can edit/create/delete team profiles
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditTeamProfiles = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};

// -- Feedbacks ----------------------------------------------------------------

/**
 * Check if user can edit/create/delete feedbacks
 * Per RBAC Matrix: Super Admin, Supreme Admin, Web Content Editor
 */
export const canEditFeedbacks = (user: User | null): boolean => {
  if (!user) return false;
  return user.isSuper || user.isSupremeAdmin || user.isWebContentEditor;
};


// =============================================================================
// FINANCE DOMAIN
// =============================================================================

/**
 * Check if user can see the Finance Summary sidebar (Invoices, Payments,
 * Orders, Refunds, CurrentEntries, Invoicer).
 * Super, and Supreme. Explicitly excludes Location Admin.
 */
export const canSeeFinanceSummary = (user: User | null): boolean => {
  if (!user) return false;
  return !!(user.isSuper || user.isSupremeAdmin || user.user_type_id === 2);
};